import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Collapse } from '@mui/material';
import ItemList from '../components/ItemList';
import ArchiveBrowser from '../components/ArchiveBrowser';
import InventoryDialogs from '../components/InventoryDialogs';
import InventoryHeaderControls from '../components/InventoryHeaderControls';
import useInventoryData from '../hooks/useInventoryData';
import useInventoryFilters from '../hooks/useInventoryFilters';
import useArchiveData from '../hooks/useArchiveData';

const InventoryPage = ({
  user,
  getAuthHeaders,
  pendingCheckout,
  showSuccessMessage,
  setError,
  channels,
  accountId,
  archiveSoldItems = false,
  fetchNextItemNumber,
  isRequestingBarcodes = false,
  requestBarcodes,
  quailhqStatuses = {},
}) => {
  const API_BACKEND = process.env.REACT_APP_SHOPKEEP_BACKEND || 'http://localhost:3001';
  const INVENTORY_URL = `${API_BACKEND}/api/inventory`;
  const SALES_TRANSACTIONS_URL = `${API_BACKEND}/api/salestransactions`;

  const {
    searchQuery,
    setSearchQuery,
    statusFilter,
    setStatusFilter,
    selectedChannelId,
    setSelectedChannelId,
  } = useInventoryFilters();
  const [inventoryPage, setInventoryPage] = useState(0);
  const [inventoryRowsPerPage, setInventoryRowsPerPage] = useState(25);
  const [showArchiveBrowser, setShowArchiveBrowser] = useState(false);
  const [printLabelItems, setPrintLabelItems] = useState(null);
  const [sortConfig, setSortConfig] = useState({ field: null, order: 'asc' });

  const {
    periods: archivePeriods,
    selectedPeriod,
    setSelectedPeriod,
    archivedItems,
    archivedItemsCount,
    quota: archiveQuota,
    isLoadingPeriods,
    isLoadingItems: isLoadingArchiveItems,
    isLoadingQuota: isLoadingArchiveQuota,
    isRestoring,
    fetchPeriods,
    fetchItems: fetchArchiveItems,
    restoreItems,
    fetchQuota: fetchArchiveQuota,
  } = useArchiveData({
    getAuthHeaders,
    showSuccessMessage,
    setError,
  });

  // Memoize callback functions to prevent re-renders of child components
  const handleSearchChange = useCallback((query) => {
    setSearchQuery(query);
  }, [setSearchQuery]);

  const handleStatusFilterChange = useCallback((status) => {
    setStatusFilter(status);
  }, [setStatusFilter]);

  const handleSelectChannel = useCallback((channelId) => {
    setSelectedChannelId(channelId);
  }, [setSelectedChannelId]);

  const handleSortChange = useCallback((field) => {
    setSortConfig(prev => {
      if (prev.field === field) {
        if (prev.order === 'asc') {
          return { field, order: 'desc' };
        }
        // desc -> clear
        return { field: null, order: 'asc' };
      }
      // new field -> asc
      return { field, order: 'asc' };
    });
  }, []);

  const {
    items,
    totalCount: inventoryTotalCount,
    isLoadingItems,
    showForm,
    setShowForm,
    bulkEntryMode,
    setBulkEntryMode,
    editingItem,
    setFormData,
    formData,
    editFormData,
    fetchItems,
    searchItems,
    markAsSold,
    markAsMissing,
    deleteItem,
    checkItemTransactions,
    startEditing,
    cancelEditing,
    handleEditInputChange,
    handleEditSubmit,
    handleInputChange,
    handleSubmit,
    quickAddItem,
    deleteItemImage,
    setMainItemImage,
    handleEditImageUpload,
    isSubmitting,
    archiveItems,
    isLoadingEditImages,
  } = useInventoryData({
    inventoryUrl: INVENTORY_URL,
    salesTransactionsUrl: SALES_TRANSACTIONS_URL,
    getAuthHeaders,
    pendingCheckout,
    showSuccessMessage,
    setError,
    accountId,
    archiveSoldItems,
  });

  const handleArchiveItem = useCallback(async (item) => {
    const result = await archiveItems([item.id]);
    if (result && showArchiveBrowser) {
      fetchPeriods();
      fetchArchiveQuota();
    }
  }, [archiveItems, showArchiveBrowser, fetchPeriods, fetchArchiveQuota]);

  const handleToggleArchiveBrowser = useCallback(() => {
    setShowArchiveBrowser(prev => {
      if (!prev) {
        fetchPeriods();
        fetchArchiveQuota();
      }
      return !prev;
    });
  }, [fetchPeriods, fetchArchiveQuota]);

  const handleSelectArchivePeriod = useCallback((period) => {
    setSelectedPeriod(period);
    fetchArchiveItems(period.year, period.month);
  }, [setSelectedPeriod, fetchArchiveItems]);

  const handleArchiveSearch = useCallback((query) => {
    if (selectedPeriod) {
      fetchArchiveItems(selectedPeriod.year, selectedPeriod.month, { search: query });
    }
  }, [selectedPeriod, fetchArchiveItems]);

  const handleRestoreItem = useCallback(async (item) => {
    if (!selectedPeriod) return;
    const result = await restoreItems(selectedPeriod.year, selectedPeriod.month, [item.id]);
    if (result) {
      fetchPeriods();
      fetchArchiveQuota();
      fetchItems();
    }
  }, [selectedPeriod, restoreItems, fetchPeriods, fetchArchiveQuota, fetchItems]);

  // Define handleAddItem after useInventoryData so showForm, setShowForm, etc. are available
  const handleAddItem = useCallback(() => {
    const willOpen = !showForm;
    setShowForm(willOpen);
    if (willOpen) {
      cancelEditing();
      if (selectedChannelId) {
        setFormData(prev => ({
          ...prev,
          salesChannelId: selectedChannelId,
        }));
      }
    }
  }, [showForm, selectedChannelId, setShowForm, cancelEditing, setFormData]);

  // "Active" and search use server-side pagination; "sold" and "missing" use client-side
  const isSearching = Boolean(searchQuery.trim());
  const useClientPagination = statusFilter === 'sold' || statusFilter === 'missing';

  // Reset to page 0 when filters or sort change
  useEffect(() => {
    setInventoryPage(0);
  }, [searchQuery, statusFilter, selectedChannelId, sortConfig]);

  // Main fetch effect — single source of truth for loading inventory
  // fetchItems/searchItems excluded from deps — not memoized, effect should only fire on filter/pagination changes
  useEffect(() => {
    if (!user || pendingCheckout) return;

    const activeSortBy = sortConfig.field || 'dateCreated';
    const activeSortOrder = sortConfig.field ? sortConfig.order : 'desc';

    if (isSearching) {
      searchItems(searchQuery, {
        limit: inventoryRowsPerPage,
        offset: inventoryPage * inventoryRowsPerPage,
        sortBy: activeSortBy,
        sortOrder: activeSortOrder,
      });
      return;
    }
    const commonParams = {
      salesChannelId: selectedChannelId || '',
      sortBy: activeSortBy,
      sortOrder: activeSortOrder,
      isSold: null,
      isMissing: null,
    };

    if (statusFilter === 'active') {
      // Server-side filter + pagination
      fetchItems({
        ...commonParams,
        isSold: false,
        limit: inventoryRowsPerPage,
        offset: inventoryPage * inventoryRowsPerPage,
      });
    } else if (statusFilter === 'sold') {
      // Server-side filter, fetch all, client-side pagination
      fetchItems({
        ...commonParams,
        isSold: true,
        limit: 10000,
        offset: 0,
      });
    } else if (statusFilter === 'missing') {
      // Fetch all items, client-side filter + pagination
      fetchItems({
        ...commonParams,
        limit: 10000,
        offset: 0,
      });
    }
  }, [ // eslint-disable-line react-hooks/exhaustive-deps
    user,
    pendingCheckout,
    searchQuery,
    statusFilter,
    selectedChannelId,
    sortConfig,
  ]);

  // Re-fetch when pagination changes (server-side "active" and search modes)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!user || pendingCheckout) return;

    const activeSortBy = sortConfig.field || 'dateCreated';
    const activeSortOrder = sortConfig.field ? sortConfig.order : 'desc';

    if (isSearching) {
      searchItems(searchQuery, {
        limit: inventoryRowsPerPage,
        offset: inventoryPage * inventoryRowsPerPage,
        sortBy: activeSortBy,
        sortOrder: activeSortOrder,
      });
      return;
    }

    if (statusFilter !== 'active') return;

    fetchItems({
      isSold: false,
      isMissing: null,
      limit: inventoryRowsPerPage,
      offset: inventoryPage * inventoryRowsPerPage,
      salesChannelId: selectedChannelId || '',
      sortBy: activeSortBy,
      sortOrder: activeSortOrder,
    });
  }, [inventoryPage, inventoryRowsPerPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleInventoryPageChange = (nextPage) => {
    setInventoryPage(nextPage);
  };

  const handleInventoryRowsPerPageChange = (nextRowsPerPage) => {
    setInventoryRowsPerPage(nextRowsPerPage);
    setInventoryPage(0);
  };

  // Client-side filtering: "missing" needs client filter,
  // search results may need status filtering within the server-paginated page
  const displayedItems = (() => {
    let result = items;
    // Apply status filter client-side when searching (within current page) or for missing filter
    if (isSearching || statusFilter === 'missing') {
      result = result.filter(item => {
        if (statusFilter === 'active') return !item.isSold && !item.isMissing;
        if (statusFilter === 'sold') return item.isSold;
        if (statusFilter === 'missing') return item.isMissing;
        return true;
      });
    }
    // Apply channel filter client-side for client-paginated modes
    if (useClientPagination && selectedChannelId) {
      result = result.filter(item => item.salesChannelId === selectedChannelId);
    }
    return result;
  })();
  const pagedItems = useClientPagination
    ? displayedItems.slice(
        inventoryPage * inventoryRowsPerPage,
        inventoryPage * inventoryRowsPerPage + inventoryRowsPerPage
      )
    : displayedItems;
  const displayCount = useClientPagination
    ? displayedItems.length
    : (inventoryTotalCount || displayedItems.length);

  const availableChannels = useMemo(() => channels.slice(), [channels]);
  const channelsMap = useMemo(() => Object.fromEntries(channels.map(c => [c.id, c.name])), [channels]);

  const handlePrintLabel = useCallback((itemOrItems, isBulk = false) => {
    const itemsArray = isBulk ? itemOrItems : [itemOrItems];
    const labelData = itemsArray
      .filter(item => item.itemNumber)
      .map(item => {
        const channel = channels.find(ch => ch.id === item.salesChannelId);
        const barcodeFormatSetting = channel?.settings?.find(s => s.key === 'barcodeFormat');
        const barcodeFormat = barcodeFormatSetting?.value || 'Code 128';
        const barcodeRequiredSetting = channel?.settings?.find(s => s.key === 'barcodeRequired');
        const barcodeRequired = barcodeRequiredSetting?.value === true;
        const barcodeSourceSetting = channel?.settings?.find(s => s.key === 'barcodeSource');
        const barcodeSource = barcodeSourceSetting?.value || 'itemNumber';
        const barcodeValue = item[barcodeSource] || item.itemNumber;
        const isQuailHQ = channel?.settings?.some(s => s.key === 'quailhqEnabled' && s.value === true) || false;
        return {
          itemNumber: item.itemNumber,
          itemTitle: item.itemTitle,
          listPrice: item.listPrice,
          barcodeFormat,
          barcodeRequired,
          barcodeValue,
          channelName: channel?.name || '',
          location: channel?.defaultLocation || '',
          isQuailHQ,
        };
      });
    if (labelData.length > 0) {
      setPrintLabelItems(labelData);
    }
  }, [channels]);

  const handleClosePrintLabel = useCallback(() => {
    setPrintLabelItems(null);
  }, []);

  const labelRequiredChannelIds = useMemo(() => {
    return new Set(
      channels
        .filter(ch => Array.isArray(ch.settings) && ch.settings.some(s => s.key === 'labelRequired' && s.value === true))
        .map(ch => ch.id)
    );
  }, [channels]);

  const quailhqChannelIds = useMemo(() => {
    return new Set(
      channels
        .filter(ch => Array.isArray(ch.settings) && ch.settings.some(s => s.key === 'quailhqEnabled' && s.value === true))
        .map(ch => ch.id)
    );
  }, [channels]);

  const handleRequestBarcodes = useCallback(async (itemIds) => {
    if (!requestBarcodes || itemIds.length === 0) return;
    // Derive channelId from the first item
    const firstItem = items.find(i => itemIds.includes(i.id));
    if (!firstItem?.salesChannelId) return;
    const result = await requestBarcodes(firstItem.salesChannelId, itemIds);
    if (result) {
      fetchItems();
    }
  }, [requestBarcodes, items, fetchItems]);

  const statusLabels = { active: 'active', sold: 'sold', missing: 'missing' };
  const hideMessage = useMemo(() => {
    const label = statusLabels[statusFilter] || 'matching';
    if (selectedChannelId) {
      return `No ${label} items found in "${channelsMap[selectedChannelId] || selectedChannelId}". Try adjusting your filters or add new items!`;
    }
    if (statusFilter === 'active') return 'No active items found. All items are sold or add your first item above!';
    if (statusFilter === 'sold') return 'No sold items found.';
    if (statusFilter === 'missing') return 'No missing items found.';
    return 'No inventory items found. Add your first item above!';
  }, [statusFilter, selectedChannelId, channelsMap]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <InventoryDialogs
        isInventory
        showForm={showForm}
        bulkEntryMode={bulkEntryMode}
        setBulkEntryMode={setBulkEntryMode}
        setShowForm={setShowForm}
        formData={formData}
        onInputChange={handleInputChange}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        channels={channels}
        selectedChannelId={selectedChannelId}
        editingItem={editingItem}
        editFormData={editFormData}
        onEditInputChange={handleEditInputChange}
        onEditSubmit={handleEditSubmit}
        onCancelEdit={cancelEditing}
        onImageUpload={handleEditImageUpload}
        onImageDelete={(image) => deleteItemImage(editingItem.id, image.storagePath)}
        onSetMainImage={(image) => setMainItemImage(editingItem.id, image.url)}
        fetchNextItemNumber={fetchNextItemNumber}
        onPrintLabel={handlePrintLabel}
        labelRequiredChannelIds={labelRequiredChannelIds}
        printLabelItems={printLabelItems}
        onClosePrintLabel={handleClosePrintLabel}
        isLoadingEditImages={isLoadingEditImages}
      />
      <InventoryHeaderControls
        itemCount={displayCount}
        onAddItem={handleAddItem}
        selectedChannelId={selectedChannelId}
        onQuickAdd={quickAddItem}
        channelsMap={channelsMap}
        channels={channels}
      />
      <Collapse in={showArchiveBrowser}>
        <ArchiveBrowser
          periods={archivePeriods}
          selectedPeriod={selectedPeriod}
          onSelectPeriod={handleSelectArchivePeriod}
          archivedItems={archivedItems}
          archivedItemsCount={archivedItemsCount}
          onRestoreItem={handleRestoreItem}
          onSearch={handleArchiveSearch}
          isLoadingPeriods={isLoadingPeriods}
          isLoadingItems={isLoadingArchiveItems}
          isRestoring={isRestoring}
          quota={archiveQuota}
          isLoadingQuota={isLoadingArchiveQuota}
        />
      </Collapse>
      <ItemList
        displayedItems={pagedItems}
        totalCount={displayCount}
        page={inventoryPage}
        rowsPerPage={inventoryRowsPerPage}
        onPageChange={handleInventoryPageChange}
        onRowsPerPageChange={handleInventoryRowsPerPageChange}
        isSearching={isSearching}
        isLoading={isLoadingItems}
        startEditing={startEditing}
        markAsSold={markAsSold}
        markAsMissing={markAsMissing}
        deleteItem={deleteItem}
        checkItemTransactions={checkItemTransactions}
        archiveItem={handleArchiveItem}
        hideMessage={hideMessage}
        channelsMap={channelsMap}
        searchQuery={searchQuery}
        onSearchChange={handleSearchChange}
        statusFilter={statusFilter}
        onStatusFilterChange={handleStatusFilterChange}
        selectedChannelId={selectedChannelId}
        onSelectChannel={handleSelectChannel}
        availableChannels={availableChannels}
        showArchiveBrowser={showArchiveBrowser}
        onToggleArchiveBrowser={handleToggleArchiveBrowser}
        onPrintLabel={handlePrintLabel}
        labelRequiredChannelIds={labelRequiredChannelIds}
        sortField={sortConfig.field}
        sortOrder={sortConfig.order}
        onSortChange={handleSortChange}
        onRequestBarcodes={handleRequestBarcodes}
        isRequestingBarcodes={isRequestingBarcodes}
        quailhqChannelIds={quailhqChannelIds}
      />
    </>
  );
};

export default InventoryPage;
